import { useState } from "react";
import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { Button, LinkButton } from "@/ui/Button";
import { MicIcon } from "@/ui/MicIcon";
import { Page } from "@/ui/Page";
import { Orb } from "@/ui/Orb";
import { Display, Tiny } from "@/ui/Text";
import { Paywall } from "@/features/plan/Paywall";
import { WALL_VOICE_MONTH_USED, type WallCopy } from "@/features/plan/paywall-copy";
import { canTalk, usePlan } from "@/features/plan/use-plan";

/**
 * The orb, one line about what it does, and the button that starts it, all
 * in the middle of the screen. Typing is the quiet alternative right under it.
 */
export function TalkTab() {
  const navigate = useNavigate();
  const plan = usePlan();
  const { analytics } = useServices();
  const [wall, setWall] = useState<WallCopy | null>(null);

  /** A locked account goes to the paywall; a subscriber out of voice can still type. */
  function gate(to: "/talk/voice" | "/talk/ask") {
    analytics.track(to === "/talk/voice" ? { name: "speak tapped", from: "talk" } : { name: "type tapped" });
    if (plan.data?.locked) {
      navigate("/subscribe");
      return;
    }
    if (to === "/talk/voice" && plan.data && !canTalk(plan.data)) {
      analytics.track({ name: "voice limit shown" });
      setWall(WALL_VOICE_MONTH_USED);
      return;
    }
    navigate(to);
  }
  const talk = () => gate("/talk/voice");

  return (
    <Page middle>
      <div className="flex flex-col items-center text-center">
        <button type="button" aria-label="Start a conversation" aria-busy={plan.isPending || undefined} onClick={talk} className="rounded-full">
          <Orb size={116} floating />
        </button>
        <Display className="mt-7">Ask her anything.</Display>
        <Tiny className="mt-3 max-w-[300px]">
          Talk out loud, in English or Hindi. You can cut in or change your mind halfway, and she’ll follow.
        </Tiny>
        <div className="mt-8 flex w-full flex-col items-center gap-3">
          <Button label="Tap to speak to Antar" icon={<MicIcon />} onClick={talk} />
          <LinkButton label="Type instead" onClick={() => gate("/talk/ask")} />
        </div>
      </div>
      <Paywall copy={wall} onClose={() => { setWall(null); void plan.refetch(); }} />
    </Page>
  );
}
